import type { ReactiveController, ReactiveControllerHost } from 'lit';

/**
 * "Just moved here" flash (Anke, 2026-09-06: "damit man noch sehen kann,
 * welches issue ich gerade verschoben habe") — a row appears INSTANTLY in a
 * highlighted color when an Issue lands in a column/cell via drag&drop, then
 * fades back to normal over 2s. Two-phase (`isJustMoved()` true first, then
 * `isFading()` true one frame later) so the highlight appears instantly but
 * only the fade-OUT is slow — `flash()` schedules the phase switch itself.
 *
 * Extracted (2026-09-07, review-fund docs/feature-plans/0031) from THREE
 * near-identical private copies of this exact state machine
 * (`my-organizer-label-column`, `my-organizer-assigned-column`,
 * `my-organizer-calendar-day`) into one Reactive Controller — Composition,
 * not Inheritance, per ADR-007 §2/§6 (the same reasoning that put
 * `RxjsLifecycleController` here instead of on a shared base class). The
 * third copy (added for the calendar day cell) was the trigger to finally
 * extract it, per the house "reuse-before-reimplement" paradigm.
 *
 * A plain private class field, NOT `@state()` — this controller is not a
 * Lit element itself, so it calls `host.requestUpdate()` explicitly on every
 * state change instead (the same thing `@state()`'s setter does internally).
 *
 * @example
 * ```ts
 * class MyColumn extends AbstractO2dElement {
 *     private readonly _justMoved = new JustMovedFlashController(this);
 *
 *     private _onDrop(issue: Issue) {
 *         // ... persist ...
 *         this._justMoved.flash(issue.uid);
 *     }
 *
 *     private _renderRow(issue: Issue) {
 *         return html`<div class=${classMap({
 *             'row--just-moved': this._justMoved.isJustMoved(issue.uid),
 *             'row--just-moved-fading': this._justMoved.isFading(issue.uid),
 *         })}>...</div>`;
 *     }
 * }
 * ```
 */
export class JustMovedFlashController implements ReactiveController {

    private _issueUid: string | null = null;
    private _fading = false;

    constructor(private readonly host: ReactiveControllerHost) {
        host.addController(this);
    }

    /** Required by `ReactiveController` — no lifecycle hookup needed on connect itself. */
    public hostConnected(): void {
        // no-op
    }

    /** Resets on disconnect so a reconnected (e.g. route-navigated-back-to)
     *  host doesn't resume mid-flash for an Issue that may no longer even
     *  be shown. */
    public hostDisconnected(): void {
        this._issueUid = null;
        this._fading = false;
    }

    public isJustMoved(issueUid: string): boolean {
        return this._issueUid === issueUid && !this._fading;
    }

    public isFading(issueUid: string): boolean {
        return this._issueUid === issueUid && this._fading;
    }

    public flash(issueUid: string): void {
        this._issueUid = issueUid;
        this._fading = false;
        this.host.requestUpdate();

        const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: () => void) => setTimeout(cb, 0);
        raf(() => {
            if (this._issueUid !== issueUid) {
                return; // superseded by a newer flash in the meantime.
            }
            this._fading = true;
            this.host.requestUpdate();

            setTimeout(() => {
                if (this._issueUid === issueUid) {
                    this._issueUid = null;
                    this._fading = false;
                    this.host.requestUpdate();
                }
            }, 2000);
        });
    }
}
